import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils";
import { Spinner } from "./spinner";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-base whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary: "bg-primary text-surface hover:bg-primary-hover",
        outline:
          "bg-surface text-primary border border-primary-border hover:bg-primary-soft hover:border-primary",
        bulk: "bg-canvas text-ink-subtle border border-border-strong hover:bg-surface",
        "bulk-active":
          "bg-primary text-surface border border-primary hover:bg-primary-hover",
        text: "bg-transparent text-ink-subtle hover:text-ink-muted",
        danger: "bg-danger text-surface hover:opacity-90",
      },
      size: {
        sm: "h-[26px] px-[10px] text-[11px] font-semibold rounded-sm",
        md: "h-[34px] px-[14px] text-xs font-medium",
        lg: "h-[44px] px-[18px] text-sm font-semibold tracking-tight",
      },
    },
    defaultVariants: { variant: "primary", size: "lg" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild, loading, disabled, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? <Spinner size={size === "sm" ? 11 : 15} /> : children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
